// useMapAiSuggest — KI-Vorschlag-Flow des Mapping-Editors (Start + Polling + Uebernahme ins
// Formular + Verwerfen). Ausgelagert aus AbMappingEditor.js (500-LOC-Regel). Polling-Muster wie
// components/AbSuggestPanel.js (Cleanup-Return, 4s-Intervall, 15-Min-Timeout).
//
// DOKUMENTIERTE Abweichung vom Bauplan-Wortlaut ("Poll via getSuggestJob — Ergebnis fuellt das
// Formular"): GET /suggest-jobs/:jobId liefert NUR eine reduzierte Vorschau (matched/lineCount/
// lines/warnings/model), OHNE ruleset_json/supplier_id (anders als OC-Pilots eigenes Backend;
// api.js darf laut Auftrag nicht geaendert werden). Dieser Hook nutzt deshalb den vorhandenen Pfad
// POST /suggest-jobs/:jobId/apply (legt einen inaktiven Regelset-Entwurf an) + GET /rulesets/:id,
// um das Formular zu befuellen — "verwerfen" loescht diesen Entwurf wieder (nur inaktive Versionen
// sind loeschbar, passt zur Statusmaschine). S. Verifikationsbericht des Agenten fuer Details.

import { useEffect, useRef, useState } from 'react';
import { fromRulesetJson, toRulesetObject } from './rulesetModel';
import { startSuggest, getSuggestJob, applySuggest, deleteRuleset, getRuleset, previewRuleset } from '../api';

const AI_POLL_INTERVAL_MS = 4000;
const AI_MAX_POLL_MS = 15 * 60 * 1000;

/**
 * restoreBaseline: async () => void — vom Aufrufer bereitgestellt, stellt bei "verwerfen" den
 * Formular-Stand VOR dem KI-Lauf wieder her (Einstieg a: aktives Regelset des Testdokuments neu
 * laden; Einstieg b: das urspruenglich uebergebene ruleset-Prop) — s. AbMappingEditor.js.
 */
export default function useMapAiSuggest({ user, t, testDocumentId, setForm, setLoadedMeta, setPreview, showSuccess, restoreBaseline }) {
  const [aiJobId, setAiJobId] = useState(null);
  const [aiPhase, setAiPhase] = useState('idle'); // idle|starting|running|done|error|not_configured
  const [aiError, setAiError] = useState(null);
  const [aiMsg, setAiMsg] = useState(null);
  const [aiDraftRulesetId, setAiDraftRulesetId] = useState(null);
  const aiStartedAtRef = useRef(null);
  const aiTimerRef = useRef(null);
  // Spiegel des Draft-Zustands fuer den Unmount-Cleanup: verlaesst der Nutzer den Editor, ohne den
  // KI-Entwurf zu uebernehmen oder zu verwerfen, wuerde der inaktive Entwurf sonst verwaisen.
  const draftRef = useRef(null);
  useEffect(() => { draftRef.current = aiDraftRulesetId; }, [aiDraftRulesetId]);
  useEffect(() => () => {
    if (draftRef.current) deleteRuleset(user, draftRef.current).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const finishAiSuggest = async (jobRes) => {
    try {
      const applied = await applySuggest(user, aiJobId);
      const full = await getRuleset(user, applied.rulesetId);
      const loaded = fromRulesetJson(full.rulesJson);
      setForm(loaded);
      setAiDraftRulesetId(applied.rulesetId);
      setLoadedMeta({ id: full.id, version: full.version });
      const r = jobRes.result || {};
      const warn = Array.isArray(r.warnings) && r.warnings.length ? ` ${t('ab.map.aiSuggestWarnings', { warnings: r.warnings.join('; ') })}` : '';
      const key = r.matched ? 'ab.map.aiSuggestBanner' : 'ab.map.aiSuggestBannerNoMatch';
      setAiMsg(t(key, { model: r.model || '—', lineCount: r.lineCount ?? 0, supplier: full.supplierId }) + warn);
      setAiPhase('done');
      if (testDocumentId) {
        try { setPreview(await previewRuleset(user, { rulesJson: toRulesetObject(loaded), documentId: testDocumentId })); } catch { /* Vorschau best-effort */ }
      }
    } catch (e) {
      setAiError(e.message);
      setAiPhase('error');
    }
  };

  useEffect(() => {
    if (aiPhase !== 'running' || !aiJobId) return undefined;
    let stopped = false;
    const poll = async () => {
      if (stopped) return;
      if (Date.now() - aiStartedAtRef.current > AI_MAX_POLL_MS) { setAiError(t('ab.map.aiSuggestTimeout')); setAiPhase('error'); return; }
      try {
        const res = await getSuggestJob(user, aiJobId);
        if (stopped) return;
        if (res?.status === 'done') { await finishAiSuggest(res); }
        else if (res?.status === 'error') { setAiError(res?.error || t('ab.map.aiSuggestFailed')); setAiPhase('error'); }
        else { aiTimerRef.current = setTimeout(poll, AI_POLL_INTERVAL_MS); }
      } catch (e) {
        if (stopped) return;
        setAiError(e.message); setAiPhase('error');
      }
    };
    poll();
    return () => { stopped = true; if (aiTimerRef.current) clearTimeout(aiTimerRef.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aiPhase, aiJobId, user]);

  const startAiSuggest = async () => {
    if (!testDocumentId) return;
    setAiError(null); setAiMsg(null); setAiPhase('starting');
    try {
      const res = await startSuggest(user, testDocumentId);
      aiStartedAtRef.current = Date.now();
      setAiJobId(res.jobId);
      setAiPhase('running');
    } catch (e) {
      if (e.status === 503) setAiPhase('not_configured');
      else if (e.status === 409) { setAiError(t('ab.map.aiSuggestAlreadyRunning')); setAiPhase('error'); }
      else { setAiError(e.message); setAiPhase('error'); }
    }
  };

  const discardAiSuggestion = async () => {
    setAiError(null);
    if (aiDraftRulesetId) {
      try { await deleteRuleset(user, aiDraftRulesetId); } catch { /* z. B. bereits aktiviert/geloescht -> Formular wird trotzdem zurueckgesetzt */ }
    }
    setAiDraftRulesetId(null);
    setAiJobId(null);
    setAiPhase('idle');
    setAiMsg(null);
    await restoreBaseline();
    showSuccess?.(t('ab.map.aiSuggestDiscardedMsg'));
  };

  // Wird beim erfolgreichen "Speichern & aktivieren" aufgerufen, damit ein verbrauchter KI-Entwurf
  // nicht als loeschbarer "Geist" stehen bleibt (Aufrufer prueft selbst, ob die aktivierte ID
  // vom Entwurf abweicht).
  const clearAiDraftRef = (activatedId) => {
    if (aiDraftRulesetId && aiDraftRulesetId !== activatedId) {
      deleteRuleset(user, aiDraftRulesetId).catch(() => {});
    }
    setAiDraftRulesetId(null);
  };

  return { aiPhase, aiMsg, aiError, aiDraftRulesetId, startAiSuggest, discardAiSuggestion, clearAiDraftRef };
}
